import { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import { ColumnDefinition, QueryParams } from '../types';

interface CustomViewColumn {
  id?: string;
  label?: string;
  type?: string;
  itemname?: string;
  isVisible?: boolean;
  isFilter?: boolean;
}

interface CustomViewDefinition {
  columns?: CustomViewColumn[];
}

/**
 * Merges base columns with Custom View columns based on FIELDS in queryParams.
 * 
 * When a Custom View is selected, queryParams.FIELDS.field contains the list of
 * visible column itemnames. This function filters and orders columns accordingly.
 * 
 * Rules:
 * 1. If FIELDS is set, only show columns matching those fields (in FIELDS order)
 * 2. If no FIELDS, return base columns unchanged
 * 3. Column labels come from Custom View definition, then base columns, then field name
 * 
 * @param baseColumns - Base column definitions from FormSettings
 * @param customViews - Array of available custom views (used for labels)
 * @param queryParams - Current query parameters containing FIELDS
 * @returns Filtered and ordered column definitions array
 */
export function mergeColumns(
  baseColumns: ColumnDefinition[],
  customViews: CustomView[],
  queryParams: QueryParams,
): ColumnDefinition[] {
  // If no base columns, return empty array
  if (!baseColumns || baseColumns.length === 0) {
    return [];
  }

  // Check if FIELDS is set (indicates Custom View is selected)
  const fields = queryParams.FIELDS?.field;
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    // No Custom View selected, return base columns unchanged
    return baseColumns;
  }

  // Create a map of base columns by key for fast lookup
  const baseColumnsByKey = new Map<string, ColumnDefinition>();
  baseColumns.forEach(col => {
    baseColumnsByKey.set(col.key, col);
  });

  // Build a map of Custom View column labels by itemname
  const customViewLabels = new Map<string, string>();
  const customViewNames = new Map<string, string>();

  for (const view of customViews) {
    if (view.definition) {
      try {
        const def: CustomViewDefinition = JSON.parse(view.definition);
        if (def.columns) {
          for (const col of def.columns) {
            if (col.itemname && col.label) {
              customViewLabels.set(col.itemname, col.label);

              // Derive the effective field name generically:
              // 1) If itemname is in form "field|fieldName", use the part after "|"
              // 2) Else if type is in form "field|fieldName", use the part after "|"
              // 3) Otherwise, fall back to the raw itemname
              let effectiveName = col.itemname;

              if (col.itemname.includes('|')) {
                const [, right] = col.itemname.split('|');
                if (right && right.trim() !== '') {
                  effectiveName = right;
                }
              } else if (col.type && col.type.includes('|')) {
                const [, rightType] = col.type.split('|');
                if (rightType && rightType.trim() !== '') {
                  effectiveName = rightType;
                }
              }

              customViewNames.set(col.itemname, effectiveName);
            }
          }
        }
      } catch {
        // Ignore invalid JSON
      }
    }
  }

  // Build columns array in FIELDS order, using base column labels when available
  const result: ColumnDefinition[] = [];
  
  for (const fieldName of fields) {
    // Try to find matching base column
    const baseCol = baseColumnsByKey.get(fieldName);
    
    if (baseCol) {
      // Use base column (has proper label)
      result.push(baseCol);
    } else {
      // Field not in base columns - create a column entry
      // Use Custom View label if available, otherwise use field name
      const label = customViewLabels.get(fieldName) ?? fieldName;
      const name = customViewNames.get(fieldName) ?? fieldName;
      result.push({
        key: name,
        name: label,
      });
    }
  }

  return result;
}

